export function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x))
}

// Shortest toroidal difference (a − b) in (-span/2, span/2] for axis length `span`.
export function wrappedDelta(a, b, span) {
  let delta = a - b;
  if (delta > span / 2) delta -= span;
  else if (delta < -span / 2) delta += span;
  return delta
}

// Shortest signed angle delta in degrees, in (-180, 180].
export function shortestAngleDeltaDeg(d) {
  let v = d % 360;
  if (v > 180) v -= 360;
  else if (v < -180) v += 360;
  return v
}

// Shortest-arc angular lerp, degrees.
export function lerpAngleDeg(a, b, t) {
  let diff = ((b - a) % 360 + 540) % 360 - 180;
  return a + diff * t
}

// Lerp on a toroidal axis of size `span`. Handles a wrapped shortest path.
export function lerpWrapped(a, b, t, span) {
  const delta = wrappedDelta(b, a, span);
  const v = a + delta * t;
  return ((v % span) + span) % span
}

// Wrap `v` into primary range [0, span) for a toroidal axis.
export function torusCanon(v, span) {
  if (!Number.isFinite(v) || !Number.isFinite(span) || span <= 0) return v;
  return ((v % span) + span) % span
}

/**
 * Like {@link lerpWrapped} with optional padding so interpolation can favor
 * paths away from raw 0/span edges (used for ship interp with radius pad).
 */
export function lerpWrappedWithPad(a, b, t, span, pad = 0) {
  if (!pad) return lerpWrapped(a, b, t, span);

  const paddedSpan = span + pad * 2;
  let from = a + pad;
  let to = b + pad;
  let delta = to - from;

  if (delta > paddedSpan / 2) delta -= paddedSpan;
  else if (delta < -paddedSpan / 2) delta += paddedSpan;

  const value = from + delta * t;
  return ((value % paddedSpan) + paddedSpan) % paddedSpan - pad
}

// Map snapshot rows by `id` for O(1) bracket lookups while interpolating.
export function indexById(arr) {
  const m = new Map();
  for (const e of arr) m.set(e.id, e);
  return m
}

// Asteroid ids on the wire may be numbers or numeric strings; use for Map keys / dedupe.
export function toFiniteAsteroidId(id) {
  const n = Number(id)
  return Number.isFinite(n) ? n : null
}

export function rotatePoint(p, center, angle) {
  return {
    x: ((p.x - center.x) * Math.cos(angle) - (p.y - center.y) * Math.sin(angle)) + center.x,
    y: ((p.x - center.x) * Math.sin(angle) + (p.y - center.y) * Math.cos(angle)) + center.y
  }
}

export function randomNumBetween(min, max) {
  return Math.random() * (max - min + 1) + min
}

// 32-bit string hash (FNV-1a style); deterministic for the same `text`.
export function hashString(text) {
  let hash = 2166136261
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/**
 * Mulberry32 PRNG factory.
 * @param {number} seed
 * @returns {() => number} function returning values in [0, 1)
 */
export function mulberry32(seed) {
  let value = seed >>> 0
  return function next() {
    value += 0x6D2B79F5
    let t = value
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function asteroidVertices(id, count, rad) {
  const rng = mulberry32(hashString(`asteroid:${id}:${count}:${rad}`))
  const p = []
  for (let i = 0; i < count; i++) {
    const angle = (360 / count) * i * Math.PI / 180
    const jitterX = (rng() < .5 ? -1 : 1) * rng() / 3
    const jitterY = (rng() < .5 ? -1 : 1) * rng() / 3
    let x = (-Math.sin(angle) + jitterX) * rad
    let y = (-Math.cos(angle) + jitterY) * rad
    const length = Math.hypot(x, y)
    if (length > rad && length > 0) {
      const scale = rad / length
      x *= scale
      y *= scale
    }
    p.push({
      x,
      y
    })
  }
  return p
}
